// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {applyIssueFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {applyIssueSummaryDelta} from '@github-ui/sub-issues/optimistic-overlay'
import {useAnalytics} from '@github-ui/use-analytics'
import {useMemo} from 'react'

import {LINKED_BRANCHES_CONNECTION_PATH} from '../mutations/create-linked-branch-mutation'
import {updateParentSubIssues} from '../mutations/parent-sub-issues-optimistic'
import {
  issueViewerSecondaryViewQueryKey,
  issueViewerSecondaryViewQueryOptions,
  type IssueViewerSecondaryViewResponse,
} from './fetch-issue-viewer-secondary-view'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import type {IssueDeletionConnection} from './OptionConfig'
import {
  BLOCKED_BY_CONNECTION_PATH,
  BLOCKING_CONNECTION_PATH,
} from './sections/relations-section/dependency-issue-query-types'
import {RELATES_TO_CONNECTION_PATH} from './sections/relations-section/relates-to-query-types'

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']
const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

type UseSecondaryQueryProps = {
  owner: string
  repo: string
  number: number
  markAsRead?: boolean
}

export function resolveHideClosedSubIssuesEnabled(
  serverFeatures: IssueViewerSecondaryViewResponse['features'] | undefined,
  clientFeatureEnabled: boolean,
): boolean {
  return serverFeatures?.hideClosedSubIssues ?? clientFeatureEnabled
}

function nestedSubIssuesInitialData(
  secondaryData: IssueViewerSecondaryViewServerQueryQuery | undefined,
): SubIssuesListItemNestedSubIssuesServerQueryQuery | undefined {
  const repository = secondaryData?.repository
  const issue = repository?.issue
  if (!repository || !issue || !issue.subIssuesConnection) return undefined

  return {
    repository: {
      id: repository.id,
      issue: {
        id: issue.id,
        subIssues: {
          nodes: issue.subIssues.nodes,
          totalCount: issue.subIssuesConnection.totalCount,
        },
      },
    },
  }
}

export function useSecondaryQuery({
  owner,
  repo,
  number,
  markAsRead = true,
}: UseSecondaryQueryProps): [
  IssueSecondaryNode | null | undefined,
  boolean,
  SubIssuesListItemNestedSubIssuesServerQueryQuery | undefined,
  IssueDeletionConnection | undefined,
  number,
] {
  const clientHideClosedSubIssuesEnabled = isFeatureEnabled('issues_hide_closed_sub_issues')
  const {sendAnalyticsEvent} = useAnalytics()

  // markAsRead is excluded from the query key because it triggers a server side effect
  // (clearing the unread notification); including it would let a cached response satisfy
  // the request, so the notification would never get marked as read.
  const secondaryViewQueryKey = issueViewerSecondaryViewQueryKey(owner, repo, number)
  const secondaryViewQueryOptions = useMemo(
    () => issueViewerSecondaryViewQueryOptions({owner, repo, number, markAsRead, sendAnalyticsEvent}),
    [markAsRead, number, owner, repo, sendAnalyticsEvent],
  )
  const {data: tanstackSecondaryViewData, dataUpdatedAt} = useQuery(secondaryViewQueryOptions)
  const initialNestedSubIssuesData = useMemo(
    () => nestedSubIssuesInitialData(tanstackSecondaryViewData),
    [tanstackSecondaryViewData],
  )

  const overlayIssueId = tanstackSecondaryViewData?.repository?.issue?.id
  const optimisticOverlayEnabled = overlayIssueId != null
  // Owner id points at `repository.issue`; the engine finds it by id anywhere in `base`, so the
  // connection path stays `['subIssues']`. `?? ''` only covers disabled passes (never a live owner).
  const subIssuesOwnerId = overlayIssueId ?? ''

  // Opt-in per connection: insert positions and count deltas only mean something where it renders.
  const optimisticConnections = useMemo(() => {
    if (!overlayIssueId) return []
    return [
      {ownerId: overlayIssueId, connectionPath: LINKED_BRANCHES_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: RELATES_TO_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: BLOCKED_BY_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: BLOCKING_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH},
      ...(optimisticOverlayEnabled ? [{ownerId: subIssuesOwnerId, connectionPath: SUB_ISSUES_CONNECTION_PATH}] : []),
    ]
  }, [optimisticOverlayEnabled, overlayIssueId, subIssuesOwnerId])
  // No `revalidate`: `SubIssue::MAXIMUM_BREADTH` caps a parent at 100, so `subIssues(first: 100)` is
  // never a partial page and the fold over the remove operation's deltas is already authoritative.
  const secondaryViewTargets = useMemo(() => [{queryKey: secondaryViewQueryKey}], [secondaryViewQueryKey])
  const mergedSecondaryViewData = useOptimisticData(tanstackSecondaryViewData, {
    // Entity operations need no connection list.
    connections: optimisticConnections,
    targets: secondaryViewTargets,
    applyEntityFieldItem: applyIssueFieldValue,
    applyEntityFieldValuesDelta: applyIssueSummaryDelta,
    updateEntityFieldItems: updateParentSubIssues,
  })

  const secondaryIssueData: IssueSecondaryNode | null | undefined = mergedSecondaryViewData?.repository?.issue
  const subIssueDeletionTargets = useMemo(
    () => [{queryKey: secondaryViewQueryKey, revalidate: true, refetchType: 'all' as const}],
    [secondaryViewQueryKey],
  )
  const subIssueDeletionConnection = useMemo<IssueDeletionConnection | undefined>(
    () =>
      overlayIssueId
        ? {
            ownerId: overlayIssueId,
            connectionPath: SUB_ISSUES_CONNECTION_PATH,
            targets: subIssueDeletionTargets,
          }
        : undefined,
    [overlayIssueId, subIssueDeletionTargets],
  )
  const hideClosedSubIssuesEnabled = resolveHideClosedSubIssuesEnabled(
    tanstackSecondaryViewData?.features,
    clientHideClosedSubIssuesEnabled,
  )
  return [
    secondaryIssueData,
    hideClosedSubIssuesEnabled,
    initialNestedSubIssuesData,
    subIssueDeletionConnection,
    dataUpdatedAt,
  ]
}
